import { HealthAssessment } from '@/types';

export const createEmptyAssessment = (
  assessmentType: 'full' | 'screening' = 'full'
): HealthAssessment => ({
  assessmentType,
  understands: { reasonForAttending: false, whoYouAre: false, dataUsage: false },
  height: '',
  weight: '',
  bmi: '',
  bloodPressure1: '',
  bloodPressure2: '',
  bloodPressure3: '',
  pulse: '',
  urinalysis: { nad: false, ph: '', ketones: false, protein: false, glucose: false, blood: false },
  usesDrugs: '' as 'yes',
  drinksAlcohol: '' as 'yes',
  hearing: { otoscopyL: '', otoscopyR: '', category: '', outcome: '' as 'acceptable' },
  visualAcuity: {
    peripheralMeetsSom: false,
    snellensWithoutL: '',
    snellensWithoutR: '',
    snellensCorrectiveL: '',
    snellensCorrectiveR: '',
    outcome: '' as 'acceptable'
  },
  colourPerception: '' as 'acceptable',
  lungFunction: '' as 'acceptable',
  msdMobility: '' as 'acceptable',
  mentalHealth: { comment: '', outcome: '' as 'acceptable' },
  generalHealth: { outcome: '' as 'acceptable', details: '' },
  medication: { hasMedication: false, details: '' },
  fitStatus: '' as 'fit',
  restrictions: [],
  recall: '',
  clinicalNotes: '',
  continuedOverleaf: false
});

const pushMissingDrugTestFields = (d: HealthAssessment, missing: string[]) => {
  if (d.donorIdChecked === undefined) missing.push('Donor ID Checked');
  if (!d.donorIdMethod) missing.push('Donor ID Method');
  if (d.drugTested === undefined) missing.push('Drug Tested');
  if (!d.drugOutcome) missing.push('Drug Test Outcome');
  if (d.alcoholTested === undefined) missing.push('Alcohol Tested');
  if (!d.alcoholOutcome) missing.push('Alcohol Test Outcome');
};

// Single source of truth for what's mandatory. Returns the human-readable labels
// of every outstanding required field so the UI can both block save and tell the
// technician exactly how many remain. In screening mode (D&A-only visit) no
// medical exam is performed, so only consent, drugs & alcohol, medication and
// the fitness determination are mandatory — the D&A test block always is.
export const getMissingFields = (
  d: HealthAssessment,
  hasDrugTest: boolean,
  isScreening = false
): string[] => {
  const missing: string[] = [];
  if (!d.understands.reasonForAttending) missing.push('Understands reason for attending');
  if (!d.understands.whoYouAre) missing.push('Understands who you are');
  if (!d.understands.dataUsage) missing.push('Understands data usage');

  if (isScreening) {
    if (!d.usesDrugs) missing.push('Uses Drugs');
    if (!d.drinksAlcohol) missing.push('Drinks Alcohol');
    pushMissingDrugTestFields(d, missing);
    if (!d.fitStatus) missing.push('Fitness determination');
    if (d.medication.hasMedication && !d.medication.details) missing.push('Medication details');
    return missing;
  }

  if (!d.height) missing.push('Height');
  if (!d.weight) missing.push('Weight');
  if (!d.bmi) missing.push('BMI');
  if (!d.bloodPressure1) missing.push('Blood Pressure 1');
  if (!d.pulse) missing.push('Pulse');
  if (!d.usesDrugs) missing.push('Uses Drugs');
  if (!d.drinksAlcohol) missing.push('Drinks Alcohol');
  if (hasDrugTest) {
    pushMissingDrugTestFields(d, missing);
  }
  if (!d.hearing.otoscopyL) missing.push('Otoscopy L');
  if (!d.hearing.otoscopyR) missing.push('Otoscopy R');
  if (!d.hearing.category) missing.push('Hearing Category');
  if (!d.hearing.outcome) missing.push('Hearing outcome');
  // Snellens readings are not always taken; only the clinical outcome is mandatory.
  if (!d.visualAcuity.outcome) missing.push('Visual Acuity outcome');
  if (!d.colourPerception) missing.push('Colour Perception');
  if (!d.lungFunction) missing.push('Lung Function');
  if (!d.msdMobility) missing.push("MSD's / Mobility");
  if (!d.mentalHealth.outcome) missing.push('Mental Health outcome');
  if (!d.generalHealth.outcome) missing.push('General Health outcome');
  if (!d.fitStatus) missing.push('Fitness determination');
  if (d.medication.hasMedication && !d.medication.details) missing.push('Medication details');
  return missing;
};
